import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LogOut,
  ShieldCheck,
  BookOpen,
  ClipboardList,
  Cookie,
  GraduationCap,
  Camera,
  UserRound,
  CakeSlice,
  ChevronLeft,
} from "lucide-react";

// Clean corridor photograph — no baked cards. Approved reference for the Main Hall.
const HALL_BACKGROUND =
  "https://customer-assets-rejwkqb3.emergentagent.net/job_4fdfd6f9-6803-4276-8258-f36ae827b1f3/artifacts/dxtppvdh_E4022CB6-1425-43B8-8529-7DE68807CEB2.png";

// 7 spaces of Anokina Studio. Order matches the approved reference layout.
// NOTE: "Открытая библиотека" intentionally has no subtitle.
const CARDS = [
  { id: "open-library", path: "/library",      title: "Открытая библиотека",       subtitle: "",                                Icon: BookOpen },
  { id: "master-order", path: "/master-order", title: "Мастер заказа",             subtitle: "Профессиональный инструмент",     Icon: ClipboardList },
  { id: "architect",    path: "/architect",    title: "Архитектор десертов",       subtitle: "Интерактивный инструмент",        Icon: CakeSlice },
  { id: "programs",     path: "/programs",     title: "Программы Anokina Studio",  subtitle: "Обучение",                        Icon: GraduationCap },
  { id: "gingerbread",  path: "/gingerbread",  title: "Пряничная мастерская",      subtitle: "Творчество и ремесло",            Icon: Cookie },
  { id: "media-studio", path: "/media-studio", title: "Медиа-студия",              subtitle: "Для соцсетей",                    Icon: Camera },
  { id: "account",      path: "/account",      title: "Мой кабинет",               subtitle: "Личное пространство",             Icon: UserRound },
];
const byId = (id) => CARDS.find((c) => c.id === id);

export default function MainHall() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const workspaceRole = user?.role === "admin" ? "admin" : user?.role === "editor" ? "editor" : null;

  // We keep the corridor as a full-screen backdrop and place staff pills
  // as light overlays in the very top-right area — they never collide with cards
  // because cards start well below the top 6% of the frame.
  return (
    <div
      className="fixed inset-0 w-full h-full overflow-hidden"
      style={{ background: "#e8dcc4" }}
      data-testid="main-hall-page"
    >
      {/* Backdrop */}
      <img
        src={HALL_BACKGROUND}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ objectPosition: "50% 40%" }}
      />
      {/* Soft warm veil for legibility on colored zones */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(255,246,232,0.12) 0%, rgba(255,246,232,0) 30%, rgba(0,0,0,0) 70%, rgba(74,59,50,0.10) 100%)",
        }}
      />

      {/* Explicit back to Welcome Door (does NOT rely on browser history — always /) */}
      <button
        onClick={() => nav("/")}
        data-testid="back-to-door-button"
        aria-label="К входной двери"
        title="К входной двери"
        className="absolute z-30 top-3 left-3 md:top-5 md:left-5 h-9 w-9 md:h-10 md:w-10 rounded-full bg-white/78 backdrop-blur border border-[#C9B79A]/60 flex items-center justify-center hover:bg-white transition-colors shadow-[0_2px_10px_rgba(74,59,50,0.14)]"
      >
        <ChevronLeft className="w-4 h-4 text-[#4A3B32]" strokeWidth={1.6} />
      </button>

      {/* Staff floating pill (admin/editor only) */}
      {workspaceRole && (
        <button
          onClick={() => nav("/admin")}
          data-testid={workspaceRole === "editor" ? "editor-workspace-entry" : "admin-workspace-entry"}
          className="absolute z-30 top-3 right-16 md:top-5 md:right-20 h-9 md:h-10 rounded-full bg-white/88 backdrop-blur border border-[#4A3B32]/40 flex items-center gap-1.5 px-3 md:px-4 hover:bg-white transition-colors shadow-[0_2px_10px_rgba(74,59,50,0.14)]"
          title={workspaceRole === "admin" ? "Панель управления" : "Панель куратора"}
        >
          <ShieldCheck className="w-3.5 h-3.5 md:w-4 md:h-4 text-[#4A3B32] shrink-0" />
          <span className="font-serif text-[12px] md:text-sm text-[#4A3B32] whitespace-nowrap">
            {workspaceRole === "admin" ? "Панель управления" : "Панель куратора"}
          </span>
        </button>
      )}
      <button
        onClick={logout}
        data-testid="logout-button"
        aria-label="Выход"
        title="Выход"
        className="absolute z-30 top-3 right-3 md:top-5 md:right-5 h-9 w-9 md:h-10 md:w-10 rounded-full bg-white/78 backdrop-blur border border-[#C9B79A]/60 flex items-center justify-center hover:bg-white transition-colors shadow-[0_2px_10px_rgba(74,59,50,0.14)]"
      >
        <LogOut className="w-4 h-4 text-[#4A3B32]" />
      </button>

      {/* Card composition — mobile-first grid.
          Paired cards are ~square (aspect-square). Architect spans both cols
          with a reduced height so it links the rows without dominating them.
          Group is vertically centred inside the scene, leaving corridor visible
          around it. */}
      <div className="relative z-10 h-full w-full flex items-center justify-center px-6 sm:px-8 pt-16 sm:pt-20 pb-6">
        <div
          className="mx-auto grid grid-cols-2 gap-[10px] sm:gap-3 w-[min(82vw,250px)] sm:w-auto sm:max-w-[520px] md:max-w-[540px] lg:max-w-[560px]"
          data-testid="main-hall-grid"
        >
          <HallCard card={byId("open-library")} />
          <HallCard card={byId("master-order")} />
          <HallCard card={byId("architect")} wide />
          <HallCard card={byId("programs")} />
          <HallCard card={byId("gingerbread")} />
          <HallCard card={byId("media-studio")} />
          <HallCard card={byId("account")} />
        </div>
      </div>
    </div>
  );
}

function HallCard({ card, wide }) {
  const nav = useNavigate();
  if (!card) return null;
  const { id, path, title, subtitle, Icon } = card;
  // Paired cards are aspect-square (1:1). Architect (wide) spans both columns
  // and uses a reduced height ~45% of its width via aspect-[2.2/1].
  return (
    <button
      type="button"
      onClick={() => nav(path)}
      data-testid={`hall-space-${id}`}
      className={`hall-card group relative flex flex-col items-center justify-center text-center rounded-2xl border border-[#4A3B32]/45 bg-[#F5EBD6]/94 hover:bg-[#FAF3E4] active:bg-[#F0E4CC] transition-colors px-2 shadow-[0_1px_2px_rgba(74,59,50,0.10)] ${
        wide ? "col-span-2 aspect-[2.5/1] md:aspect-[3.4/1] py-1.5" : "aspect-square md:aspect-[1.35/1] py-1.5"
      }`}
      style={{ backdropFilter: "blur(2px)" }}
    >
      {/* Inner hairline — double-border feel */}
      <span
        aria-hidden="true"
        className="absolute inset-[4px] rounded-xl pointer-events-none"
        style={{ border: "1px solid rgba(74,59,50,0.28)" }}
      />
      {Icon && (
        <span
          className="mb-0.5 sm:mb-1 inline-flex items-center justify-center rounded-full border border-[#4A3B32]/45 bg-white/40 w-[26px] h-[26px] sm:w-8 sm:h-8"
          aria-hidden="true"
        >
          <Icon
            className="text-[#4A3B32] w-[12px] h-[12px] sm:w-[14px] sm:h-[14px]"
            strokeWidth={1.35}
          />
        </span>
      )}
      <div className="font-serif text-[#3A2B22] text-[11px] sm:text-[13px] md:text-[14px] leading-[1.12] px-0.5">
        {title}
      </div>
      {subtitle && (
        <div className="mt-0.5 text-[8px] sm:text-[10px] tracking-[0.09em] uppercase text-[#7d6a5c] leading-tight px-0.5">
          {subtitle}
        </div>
      )}
    </button>
  );
}
